// ROOM 404: Staff Portal & Master Governance Console
// Verification Queue, Member Role Assignment, Moderation Queue, Room Permissions,
// Announcements, Missions Review, Event Creation, Invite Codes, Unmasking & Telemetry.

import React, { useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Award,
  Bell,
  Calendar,
  CheckCircle,
  Copy,
  Database,
  Download,
  Eye,
  FileCheck,
  Key,
  Layers,
  Lock,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Shield,
  ShieldAlert,
  Sparkles,
  Target,
  Trash2,
  UserCheck,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest, resolveApiUrl } from '../services/api';
import { generateInvitesCsv, triggerCsvDownload } from '../utils/csv';
import type { Room } from '../types';

interface ApplicantQueueItem {
  applicationId: string;
  userId: string;
  status: string;
  submittedAt: number;
  legalName: string;
  college: string;
  course: string;
  admissionYear: string;
  hasEvidence: boolean;
  evidenceId: string;
  evidenceUploadedAt: number;
}

interface MemberItem {
  userId: string;
  emailMasked: string;
  status: string;
  alias: string;
  memberNumber: number;
  avatarUrl: string;
  standing: string;
  cohort: string;
  totalPoints: number;
  roles: string[];
  createdAt: number;
}

interface ReportItem {
  id: string;
  reporter_id: string;
  reporter_alias?: string;
  reporter_member_number?: number;
  target_type: string;
  target_id: string;
  reason: string;
  details?: string;
  status: string;
  created_at: number;
}

interface InviteItem {
  id: string;
  code_hash: string;
  created_by: string;
  creator_alias?: string;
  max_uses: number;
  use_count: number;
  is_revoked: number;
  expires_at: number;
  created_at: number;
  role?: string;
  cohort?: string;
  notes?: string;
  batch_id?: string;
}

interface SubmissionItem {
  id: string;
  mission_id: string;
  mission_title: string;
  points: number;
  submitter_id: string;
  submitter_alias?: string;
  submitter_member_number?: number;
  submission_text: string;
  attachment_url?: string;
  status: string;
  created_at: number;
}

interface TelemetryData {
  population: {
    verifiedMembers: number;
    targetMaxPopulation: number;
    totalRegisteredUsers: number;
    capacityPercentage: number;
  };
  database: {
    totalRooms: number;
    totalMessages: number;
    totalIdeas: number;
    totalEvents: number;
    activeReports: number;
  };
  freeTierUsage: {
    workerRequests: number;
    d1RowsRead: number;
    d1RowsWritten: number;
    doRequests: number;
    percentages: {
      workerRequests: number;
      d1RowsRead: number;
      d1RowsWritten: number;
      doRequests: number;
    };
  };
  limitsStatus: {
    level: string;
    thresholdActions: string;
  };
}

export const AdminDashboard: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { session } = useAuth();
  const [activeTab, setActiveTab] = useState<
    | 'queue'
    | 'members'
    | 'reports'
    | 'rooms'
    | 'announcements'
    | 'missions'
    | 'events'
    | 'invites'
    | 'unmask'
    | 'telemetry'
  >('queue');

  // Queue state
  const [queue, setQueue] = useState<ApplicantQueueItem[]>([]);
  const [selectedAppForEvidence, setSelectedAppForEvidence] = useState<ApplicantQueueItem | null>(null);
  const [evidenceBlobUrl, setEvidenceBlobUrl] = useState<string | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [deletionProofMessage, setDeletionProofMessage] = useState<string | null>(null);

  // Members state
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMemberForRole, setSelectedMemberForRole] = useState<MemberItem | null>(null);
  const [roleToAssign, setRoleToAssign] = useState<string>('moderator');
  const [roleAction, setRoleAction] = useState<'grant' | 'revoke'>('grant');

  // Reports state
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [selectedReport, setSelectedReport] = useState<ReportItem | null>(null);

  // Rooms state
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoomForPerms, setSelectedRoomForPerms] = useState<Room | null>(null);
  const [roomPermRole, setRoomPermRole] = useState('member');
  const [canView, setCanView] = useState(true);
  const [canPost, setCanPost] = useState(true);
  const [canReply, setCanReply] = useState(true);
  const [canReact, setCanReact] = useState(true);
  const [canUpload, setCanUpload] = useState(true);

  // Announcements state
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [annTitle, setAnnTitle] = useState('');
  const [annBody, setAnnBody] = useState('');

  // Missions state
  const [submissions, setSubmissions] = useState<SubmissionItem[]>([]);
  const [newMissionTitle, setNewMissionTitle] = useState('');
  const [newMissionBrief, setNewMissionBrief] = useState('');
  const [newMissionPoints, setNewMissionPoints] = useState(50);
  const [newMissionReqs, setNewMissionReqs] = useState('');
  const [newMissionDeadlineDays, setNewMissionDeadlineDays] = useState(14);

  // Events & Trips state
  const [newEvTitle, setNewEvTitle] = useState('');
  const [newEvDesc, setNewEvDesc] = useState('');
  const [newEvLocation, setNewEvLocation] = useState('');
  const [newEvCapacity, setNewEvCapacity] = useState(50);
  const [newEvDaysAhead, setNewEvDaysAhead] = useState(7);
  const [newEvCategory, setNewEvCategory] = useState<'event' | 'trip'>('event');

  // Invites state
  const [invites, setInvites] = useState<InviteItem[]>([]);
  const [inviteUses, setInviteUses] = useState(1);
  const [inviteDays, setInviteDays] = useState(30);
  const [generatedInviteCode, setGeneratedInviteCode] = useState<string | null>(null);

  // Batch invites state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchCount, setBatchCount] = useState(10);
  const [batchRole, setBatchRole] = useState('member');
  const [batchMaxUses, setBatchMaxUses] = useState(1);
  const [batchCohort, setBatchCohort] = useState('Cohort 404');
  const [batchDays, setBatchDays] = useState(30);
  const [batchNotes, setBatchNotes] = useState('');
  const [batchConfirming, setBatchConfirming] = useState(false);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchFeedback, setBatchFeedback] = useState<string | null>(null);
  const [batchResult, setBatchResult] = useState<{
    batchId: string;
    count: number;
    role: string;
    cohort: string;
    maxUses: number;
    expiresAt: number;
    invites: Array<{
      id: string;
      code: string;
      maxUses: number;
      expiresAt: number;
      cohort: string;
      role: string;
    }>;
  } | null>(null);

  // Unmask state
  const [targetUserId, setTargetUserId] = useState('');
  const [unmaskReason, setUnmaskReason] = useState('');
  const [unmaskCaseId, setUnmaskCaseId] = useState('');
  const [mfaConfirmed, setMfaConfirmed] = useState(true);
  const [unmaskedData, setUnmaskedData] = useState<any>(null);
  const [unmaskError, setUnmaskError] = useState<string | null>(null);

  // Telemetry
  const [telemetry, setTelemetry] = useState<TelemetryData | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const fetchQueue = async () => {
    try {
      const data = await apiRequest<{ queue: ApplicantQueueItem[] }>('/api/verifier/queue');
      setQueue(data.queue || []);
    } catch {
      // Ignored
    }
  };

  const fetchMembers = async () => {
    try {
      const data = await apiRequest<{ members: MemberItem[] }>('/api/admin/members');
      setMembers(data.members || []);
    } catch {
      // Ignored
    }
  };

  const fetchReports = async () => {
    try {
      const data = await apiRequest<{ reports: ReportItem[] }>('/api/reports');
      setReports(data.reports || []);
    } catch {
      // Ignored
    }
  };

  const fetchRooms = async () => {
    try {
      const data = await apiRequest<{ rooms: Room[] }>('/api/rooms');
      setRooms(data.rooms || []);
    } catch {
      // Ignored
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const data = await apiRequest<{ announcements: any[] }>('/api/announcements');
      setAnnouncements(data.announcements || []);
    } catch {
      // Ignored
    }
  };

  const fetchSubmissions = async () => {
    try {
      const data = await apiRequest<{ submissions: SubmissionItem[] }>('/api/missions/submissions');
      setSubmissions(data.submissions || []);
    } catch {
      // Ignored
    }
  };

  const fetchInvites = async () => {
    try {
      const data = await apiRequest<{ invites: InviteItem[] }>('/api/admin/invites');
      setInvites(data.invites || []);
    } catch {
      // Ignored
    }
  };

  const fetchTelemetry = async () => {
    try {
      const data = await apiRequest<{ telemetry: TelemetryData }>('/api/metrics');
      setTelemetry(data.telemetry);
    } catch {
      // Ignored
    }
  };

  useEffect(() => {
    fetchQueue();
    fetchMembers();
    fetchReports();
    fetchRooms();
    fetchAnnouncements();
    fetchSubmissions();
    fetchInvites();
    fetchTelemetry();
  }, []);

  const flashMessage = (msg: string) => {
    setStatusMessage(msg);
    setTimeout(() => setStatusMessage(null), 4000);
  };

  // 1. Verifier Queue: Evidence Stream
  const handleViewEvidence = async (app: ApplicantQueueItem) => {
    setSelectedAppForEvidence(app);
    setEvidenceBlobUrl(null);
    try {
      const token = localStorage.getItem('room404_auth_jwt');
      const res = await fetch(resolveApiUrl(`/api/verifier/evidence/${app.applicationId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Evidence purged or unavailable');
      const blob = await res.blob();
      setEvidenceBlobUrl(URL.createObjectURL(blob));
    } catch (err: any) {
      alert(err.message || 'Failed to stream evidence');
    }
  };

  // 1b. Review Action
  const handleReviewAction = async (action: 'approved' | 'rejected' | 'requested_replacement') => {
    if (!selectedAppForEvidence) return;
    setReviewLoading(true);
    try {
      const res = await apiRequest<{
        success: boolean;
        evidenceDeleted: boolean;
        evidenceDeletedAt: number | null;
        message: string;
      }>('/api/verifier/review', {
        method: 'POST',
        body: {
          applicationId: selectedAppForEvidence.applicationId,
          action,
          notes: `Decision executed by ${session?.profile?.alias || 'Staff'}`,
        },
      });

      if (res.evidenceDeleted) {
        setDeletionProofMessage(
          `PROOF OF DELETION: Student e-ID permanently purged from private R2 at ${new Date(
            res.evidenceDeletedAt!
          ).toISOString()}`
        );
      } else {
        setDeletionProofMessage(res.message);
      }

      setTimeout(() => {
        setSelectedAppForEvidence(null);
        setEvidenceBlobUrl(null);
        setDeletionProofMessage(null);
        fetchQueue();
        fetchTelemetry();
      }, 3000);
    } catch (err: any) {
      alert(err.message || 'Review action failed');
    } finally {
      setReviewLoading(false);
    }
  };

  // 2. Role Mutation
  const handleExecuteRoleMutation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMemberForRole) return;
    try {
      await apiRequest(`/api/admin/members/${selectedMemberForRole.userId}/role`, {
        method: 'POST',
        body: {
          roleName: roleToAssign,
          action: roleAction,
        },
      });
      flashMessage(`Role '${roleToAssign}' ${roleAction}ed for ${selectedMemberForRole.alias}`);
      setSelectedMemberForRole(null);
      fetchMembers();
    } catch (err: any) {
      alert(err.message || 'Role mutation failed');
    }
  };

  // 2b. Standing Update
  const handleStandingUpdate = async (userId: string, alias: string, newStanding: 'good' | 'probation' | 'restricted') => {
    const reason = prompt(`Enter reason for updating standing of ${alias} to '${newStanding}':`, 'Staff administrative action');
    if (!reason) return;
    try {
      await apiRequest(`/api/admin/members/${userId}/standing`, {
        method: 'POST',
        body: { standing: newStanding, reason },
      });
      flashMessage(`Standing updated to ${newStanding} for ${alias}`);
      fetchMembers();
    } catch (err: any) {
      alert(err.message || 'Standing update failed');
    }
  };

  // 3. Resolve Report
  const handleResolveReportAction = async (reportId: string, status: 'closed' | 'actioned') => {
    const notes = prompt(`Notes for closing report (${status}):`, 'Resolved by moderation staff');
    try {
      await apiRequest(`/api/reports/${reportId}/resolve`, {
        method: 'POST',
        body: { status, notes: notes || undefined },
      });
      flashMessage(`Report resolved as ${status}`);
      fetchReports();
    } catch (err: any) {
      alert(err.message || 'Failed to resolve report');
    }
  };

  // 3b. Moderation Action from Report
  const handleTakeModerationAction = async (targetUserId: string, actionType: 'warning' | 'room_mute' | 'global_restriction' | 'temporary_suspension') => {
    const reason = prompt(`Reason for executing '${actionType}':`, 'Violation of community standards');
    if (!reason) return;
    try {
      await apiRequest('/api/moderation/action', {
        method: 'POST',
        body: {
          targetUserId,
          actionType,
          reason,
          durationSeconds: actionType === 'temporary_suspension' ? 86400 : 3600,
        },
      });
      flashMessage(`Moderation action '${actionType}' executed.`);
      fetchReports();
      fetchMembers();
    } catch (err: any) {
      alert(err.message || 'Moderation action failed');
    }
  };

  // 4. Save Room Role Permissions
  const handleSaveRoomPerms = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRoomForPerms) return;
    try {
      await apiRequest(`/api/rooms/${selectedRoomForPerms.id}/permissions`, {
        method: 'POST',
        body: {
          role: roomPermRole,
          canView,
          canPost,
          canReply,
          canReact,
          canUpload,
        },
      });
      flashMessage(`Permissions saved for room #${selectedRoomForPerms.name}`);
      setSelectedRoomForPerms(null);
    } catch (err: any) {
      alert(err.message || 'Failed to update room permissions');
    }
  };

  // 5. Post Announcement
  const handlePostAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!annTitle.trim() || !annBody.trim()) return;
    try {
      await apiRequest('/api/announcements', {
        method: 'POST',
        body: { title: annTitle.trim(), body: annBody.trim() },
      });
      flashMessage('Official announcement broadcasted.');
      setAnnTitle('');
      setAnnBody('');
      fetchAnnouncements();
    } catch (err: any) {
      alert(err.message || 'Failed to broadcast announcement');
    }
  };

  // 5b. Delete Announcement
  const handleDeleteAnnouncementAction = async (id: string) => {
    if (!confirm('Remove this official announcement?')) return;
    try {
      await apiRequest(`/api/announcements/${id}`, { method: 'DELETE' });
      flashMessage('Announcement removed.');
      fetchAnnouncements();
    } catch (err: any) {
      alert(err.message || 'Failed to delete announcement');
    }
  };

  // 6. Create Mission
  const handleCreateMissionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMissionTitle.trim() || !newMissionBrief.trim()) return;
    try {
      const now = Date.now();
      const deadline = now + newMissionDeadlineDays * 86400000;
      await apiRequest('/api/missions', {
        method: 'POST',
        body: {
          title: newMissionTitle.trim(),
          brief: newMissionBrief.trim(),
          category: 'campus',
          deadline,
          points: Number(newMissionPoints),
          submissionRequirements: newMissionReqs.trim() || 'Submit verifiable proof via text or image.',
        },
      });
      flashMessage('Society mission published.');
      setNewMissionTitle('');
      setNewMissionBrief('');
      setNewMissionReqs('');
    } catch (err: any) {
      alert(err.message || 'Failed to create mission');
    }
  };

  // 6b. Review Mission Submission
  const handleReviewSubmissionAction = async (subId: string, approved: boolean) => {
    try {
      await apiRequest(`/api/missions/submissions/${subId}/review`, {
        method: 'POST',
        body: { approved },
      });
      flashMessage(approved ? 'Submission approved and points awarded!' : 'Submission rejected.');
      fetchSubmissions();
      fetchMembers();
    } catch (err: any) {
      alert(err.message || 'Review failed');
    }
  };

  // 7. Create Event or Trip
  const handleCreateEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEvTitle.trim() || !newEvDesc.trim() || !newEvLocation.trim()) return;
    try {
      const now = Date.now();
      const eventTime = now + newEvDaysAhead * 86400000;
      const rsvpDeadline = eventTime - 86400000;
      await apiRequest('/api/events', {
        method: 'POST',
        body: {
          title: newEvTitle.trim(),
          description: newEvDesc.trim(),
          approximateLocation: newEvLocation.trim(),
          capacity: Number(newEvCapacity),
          eventTime,
          rsvpDeadline,
          category: newEvCategory,
        },
      });
      flashMessage(`${newEvCategory === 'trip' ? 'Campus Trip' : 'Society Event'} published!`);
      setNewEvTitle('');
      setNewEvDesc('');
      setNewEvLocation('');
    } catch (err: any) {
      alert(err.message || 'Failed to publish event/trip');
    }
  };

  // 8. Generate Invite
  const handleGenerateInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await apiRequest<{ success: boolean; invite: { code: string; maxUses: number; expiresAt: number } }>('/api/admin/invites', {
        method: 'POST',
        body: {
          maxUses: Number(inviteUses),
          expiresInDays: Number(inviteDays),
        },
      });
      setGeneratedInviteCode(res.invite.code);
      flashMessage(`Generated code: ${res.invite.code}`);
      fetchInvites();
    } catch (err: any) {
      alert(err.message || 'Failed to generate invite');
    }
  };

  const handleRevokeInviteAction = async (inviteId: string) => {
    if (!confirm('Revoke this invitation code?')) return;
    try {
      await apiRequest(`/api/admin/invites/${inviteId}/revoke`, { method: 'POST' });
      flashMessage('Invite code revoked.');
      fetchInvites();
    } catch (err: any) {
      alert(err.message || 'Failed to revoke invite');
    }
  };

  // Batch Invites Handlers
  const handleStartBatchGeneration = (e: React.FormEvent) => {
    e.preventDefault();
    if (batchCount < 1 || batchCount > 100) {
      setBatchError('Batch count must be between 1 and 100.');
      return;
    }
    if (batchMaxUses < 1) {
      setBatchError('Max uses must be at least 1.');
      return;
    }
    if (batchDays < 1 || batchDays > 365) {
      setBatchError('Expiration days must be between 1 and 365.');
      return;
    }
    setBatchError(null);
    setBatchConfirming(true);
  };

  const handleExecuteBatchGeneration = async () => {
    setBatchSubmitting(true);
    setBatchError(null);

    const idempotencyKey = `batch_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    try {
      const res = await apiRequest<{
        success: boolean;
        batchId: string;
        count: number;
        role: string;
        cohort: string;
        maxUses: number;
        expiresAt: number;
        invites: Array<{
          id: string;
          code: string;
          maxUses: number;
          expiresAt: number;
          cohort: string;
          role: string;
        }>;
      }>('/api/admin/invites/batch', {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
        body: {
          count: Number(batchCount),
          role: batchRole,
          maxUses: Number(batchMaxUses),
          cohort: batchCohort.trim() || 'Cohort 404',
          expiresInDays: Number(batchDays),
          notes: batchNotes.trim() || undefined,
          idempotencyKey,
        },
      });

      setBatchResult(res);
      setBatchConfirming(false);
      flashMessage(`Generated ${res.count} invites for ${res.cohort}`);
      fetchInvites();
    } catch (err: any) {
      setBatchError(err.message || 'Failed to generate batch invites');
    } finally {
      setBatchSubmitting(false);
    }
  };

  const handleExportBatchCsv = () => {
    if (!batchResult) return;
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const rows = batchResult.invites.map((inv) => ({
        code: inv.code,
        deepLink: `${origin}/?invite=${encodeURIComponent(inv.code)}`,
        maxUses: inv.maxUses,
        expiration: inv.expiresAt,
        cohort: inv.cohort,
      }));

      const csvContent = generateInvitesCsv(rows);
      const safeCohort = (batchResult.cohort || 'Cohort_404').replace(/[^a-zA-Z0-9_-]/g, '_');
      triggerCsvDownload(csvContent, `room404_invites_${safeCohort}_${Date.now()}.csv`);
      setBatchFeedback('CSV successfully generated and downloaded.');
      setTimeout(() => setBatchFeedback(null), 4000);
    } catch (err: any) {
      setBatchError(err.message || 'Failed to export CSV');
    }
  };

  const handleCopyAllDeepLinks = async () => {
    if (!batchResult) return;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const links = batchResult.invites
      .map((inv) => `${origin}/?invite=${encodeURIComponent(inv.code)}`)
      .join('\n');

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(links);
        setBatchFeedback(`Copied all ${batchResult.invites.length} deep links to clipboard!`);
        setTimeout(() => setBatchFeedback(null), 4000);
      } else {
        setBatchError('Clipboard API is unavailable in this environment.');
        setTimeout(() => setBatchError(null), 4000);
      }
    } catch (err: any) {
      setBatchError('Clipboard copy permission denied. Please export CSV instead.');
      setTimeout(() => setBatchError(null), 4000);
    }
  };

  const handleCopySingleCode = async (code: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
        setBatchFeedback(`Copied code ${code} to clipboard!`);
        setTimeout(() => setBatchFeedback(null), 3000);
      }
    } catch {
      // Ignored
    }
  };

  const handleCloseBatchModal = () => {
    setShowBatchModal(false);
    setBatchConfirming(false);
    setBatchResult(null);
    setBatchError(null);
    setBatchFeedback(null);
  };

  // 9. Audited Unmask
  const handleUnmaskIdentity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetUserId.trim() || !unmaskReason.trim()) return;
    setUnmaskError(null);
    setUnmaskedData(null);
    try {
      const res = await apiRequest('/api/identity/unmask', {
        method: 'POST',
        body: {
          targetUserId: targetUserId.trim(),
          reason: unmaskReason.trim(),
          caseId: unmaskCaseId.trim() || undefined,
          isMfaVerified: mfaConfirmed,
          lastAuthTimeEpochSeconds: Math.floor(Date.now() / 1000),
        },
      });
      setUnmaskedData(res.identity);
    } catch (err: any) {
      setUnmaskError(err.message || 'Identity unmasking rejected');
    }
  };

  const filteredMembers = members.filter((m) =>
    m.alias.toLowerCase().includes(memberSearch.toLowerCase()) ||
    String(m.memberNumber).includes(memberSearch) ||
    m.emailMasked.toLowerCase().includes(memberSearch.toLowerCase())
  );

  return (
    <div className="space-y-4 pb-28 max-w-4xl mx-auto px-3 sm:px-4 pt-3">
      {/* Top Header */}
      <div className="flex items-center justify-between pb-3 border-b border-[#1f2433]">
        <div className="flex items-center space-x-3">
          <button onClick={onBack} className="p-1.5 rounded-lg bg-[#121520] text-gray-400 hover:text-white border border-[#23293d]">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-white tracking-tight flex items-center space-x-2">
              <span>Staff Governance Console</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                ACTIVE
              </span>
            </h2>
            <p className="text-xs text-gray-400 font-mono">
              Role: {session?.roles.join(', ') || 'Administrator'}
            </p>
          </div>
        </div>
      </div>

      {statusMessage && (
        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold flex items-center space-x-2">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>{statusMessage}</span>
        </div>
      )}

      {/* Responsive Horizontal Tabs */}
      <div className="flex overflow-x-auto space-x-1 pb-1 scrollbar-none bg-[#12141c] p-1 rounded-xl border border-[#1f2433] text-xs font-semibold text-gray-400">
        {[
          { id: 'queue', label: `Queue (${queue.length})`, icon: UserCheck },
          { id: 'members', label: `Members (${members.length})`, icon: Users },
          { id: 'reports', label: `Reports (${reports.filter(r => r.status === 'new').length})`, icon: AlertTriangle },
          { id: 'rooms', label: `Rooms (${rooms.length})`, icon: MessageSquare },
          { id: 'announcements', label: 'Broadcast', icon: Bell },
          { id: 'missions', label: `Missions (${submissions.filter(s => s.status === 'submitted').length})`, icon: Target },
          { id: 'events', label: 'Events & Trips', icon: Calendar },
          { id: 'invites', label: `Invites (${invites.length})`, icon: Key },
          { id: 'unmask', label: 'Unmask ID', icon: ShieldAlert },
          { id: 'telemetry', label: 'Limits (Free)', icon: Database },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-3 py-2 rounded-lg flex items-center space-x-1.5 whitespace-nowrap transition-colors ${
                isActive ? 'bg-emerald-500 text-black font-bold' : 'hover:bg-[#1a1f30] hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* 1. Verifier Queue Tab */}
      {activeTab === 'queue' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 font-mono">Pending Admissions Queue</span>
            <button onClick={fetchQueue} className="text-xs text-emerald-400 flex items-center space-x-1 hover:underline">
              <RefreshCw className="w-3 h-3" />
              <span>Refresh</span>
            </button>
          </div>

          {queue.length === 0 ? (
            <div className="py-16 text-center text-gray-500 text-xs bg-[#12141c] rounded-2xl border border-[#1f2433]">
              All applications reviewed. Queue is currently empty!
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {queue.map((app) => (
                <div key={app.applicationId} className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] space-y-2.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white text-sm">{app.legalName}</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 uppercase">
                      {app.status}
                    </span>
                  </div>
                  <div className="text-gray-400 space-y-0.5 font-mono text-[11px]">
                    <div>College: {app.college}</div>
                    <div>Course: {app.course} ({app.admissionYear})</div>
                    <div>Submitted: {new Date(app.submittedAt).toLocaleString()}</div>
                  </div>
                  <div className="pt-2 border-t border-[#1f2433] flex items-center justify-between">
                    <span className="text-[10px] text-gray-500 font-mono">
                      {app.hasEvidence ? 'e-ID Active' : 'No e-ID'}
                    </span>
                    <button
                      onClick={() => handleViewEvidence(app)}
                      className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30 rounded-xl text-xs font-semibold flex items-center space-x-1"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>Review Evidence</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 2. Members & Role Assignment Tab */}
      {activeTab === 'members' && (
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Search alias, #number, or email..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-[#12141c] border border-[#1f2433] rounded-xl text-xs text-white placeholder-gray-500 outline-none focus:border-emerald-500"
              />
            </div>
            <button onClick={fetchMembers} className="text-xs text-emerald-400 flex items-center space-x-1 hover:underline">
              <RefreshCw className="w-3 h-3" />
              <span>Refresh Directory</span>
            </button>
          </div>

          <div className="space-y-2">
            {filteredMembers.map((m) => (
              <div
                key={m.userId}
                className="p-3.5 rounded-xl bg-[#12141c] border border-[#1f2433] flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
              >
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-white">{m.alias}</span>
                    <span className="font-mono text-gray-400">#{m.memberNumber || '---'}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono border ${
                      m.standing === 'restricted'
                        ? 'bg-rose-500/10 text-rose-400 border-rose-500/30'
                        : m.standing === 'probation'
                        ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                        : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                    }`}>
                      {m.standing.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-gray-400 text-[11px] font-mono">
                    <span>{m.emailMasked}</span> • <span className="text-amber-400">{m.totalPoints} pts</span>
                  </div>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {m.roles.map((r) => (
                      <span key={r} className="px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 text-[10px] font-mono">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-2 self-end sm:self-center">
                  <button
                    onClick={() => setSelectedMemberForRole(m)}
                    className="px-2.5 py-1.5 rounded-lg bg-[#1a1f30] hover:bg-[#23293d] text-emerald-400 text-xs font-semibold border border-emerald-500/20 flex items-center space-x-1"
                  >
                    <Key className="w-3.5 h-3.5" />
                    <span>Roles</span>
                  </button>
                  {m.standing === 'restricted' ? (
                    <button
                      onClick={() => handleStandingUpdate(m.userId, m.alias, 'good')}
                      className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-semibold border border-emerald-500/30"
                    >
                      Unrestrict
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStandingUpdate(m.userId, m.alias, 'restricted')}
                      className="px-2.5 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs font-semibold border border-rose-500/30"
                    >
                      Restrict
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. Reports & Moderation Queue Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 font-mono">Student Safety & Policy Reports</span>
            <button onClick={fetchReports} className="text-xs text-emerald-400 flex items-center space-x-1 hover:underline">
              <RefreshCw className="w-3 h-3" />
              <span>Refresh</span>
            </button>
          </div>

          {reports.length === 0 ? (
            <div className="py-16 text-center text-gray-500 text-xs bg-[#12141c] rounded-2xl border border-[#1f2433]">
              No active reports in moderation queue.
            </div>
          ) : (
            <div className="space-y-2.5">
              {reports.map((rep) => (
                <div key={rep.id} className="p-4 rounded-xl bg-[#12141c] border border-[#1f2433] space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-white">
                      Target: <span className="text-amber-400 font-mono">{rep.target_type} ({rep.target_id.slice(0, 12)}...)</span>
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono border ${
                      rep.status === 'new' ? 'bg-rose-500/10 text-rose-400 border-rose-500/30' : 'bg-gray-800 text-gray-400 border-gray-700'
                    }`}>
                      {rep.status.toUpperCase()}
                    </span>
                  </div>

                  <p className="text-gray-300 font-medium">{rep.reason}</p>
                  {rep.details && <p className="text-gray-400 text-[11px] bg-[#090a0f] p-2 rounded-lg border border-[#1f2433]">{rep.details}</p>}

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-[#1f2433] text-[11px] font-mono text-gray-500">
                    <span>Filed: {new Date(rep.created_at).toLocaleString()}</span>
                    {rep.status === 'new' && (
                      <div className="flex items-center space-x-1.5">
                        <button
                          onClick={() => handleTakeModerationAction(rep.target_id, 'room_mute')}
                          className="px-2 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded hover:bg-amber-500/20"
                        >
                          Mute User
                        </button>
                        <button
                          onClick={() => handleTakeModerationAction(rep.target_id, 'global_restriction')}
                          className="px-2 py-1 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded hover:bg-rose-500/20"
                        >
                          Restrict Globally
                        </button>
                        <button
                          onClick={() => handleResolveReportAction(rep.id, 'closed')}
                          className="px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/20"
                        >
                          Dismiss / Close
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 4. Rooms & Permissions Tab */}
      {activeTab === 'rooms' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 font-mono">Platform Rooms & Policy</span>
            <button onClick={fetchRooms} className="text-xs text-emerald-400 flex items-center space-x-1 hover:underline">
              <RefreshCw className="w-3 h-3" />
              <span>Refresh</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rooms.map((room) => (
              <div key={room.id} className="p-3.5 rounded-xl bg-[#12141c] border border-[#1f2433] space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-white text-sm">{room.name}</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                    {room.visibility}
                  </span>
                </div>
                <p className="text-gray-400 text-[11px] line-clamp-1">{room.description || 'No description'}</p>
                <div className="text-[11px] font-mono text-gray-500 flex justify-between">
                  <span>Mode: {room.posting_mode}</span>
                  <span>Members: {room.member_count}</span>
                </div>
                <div className="pt-2 border-t border-[#1f2433] flex justify-end">
                  <button
                    onClick={() => {
                      setSelectedRoomForPerms(room);
                      setCanView(true);
                      setCanPost(true);
                      setCanReply(true);
                      setCanReact(true);
                      setCanUpload(true);
                    }}
                    className="px-3 py-1 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-xs font-semibold flex items-center space-x-1"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span>Configure Role Permissions</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5. Broadcast Announcements Tab */}
      {activeTab === 'announcements' && (
        <div className="space-y-4">
          <form onSubmit={handlePostAnnouncement} className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] space-y-3 text-xs">
            <h3 className="font-bold text-white text-sm flex items-center space-x-2">
              <Bell className="w-4 h-4 text-emerald-400" />
              <span>Broadcast Official Society Announcement</span>
            </h3>
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Announcement Title</label>
              <input
                type="text"
                value={annTitle}
                onChange={(e) => setAnnTitle(e.target.value)}
                placeholder="e.g., Inaugural Hackathon Registration"
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Content (Markdown supported)</label>
              <textarea
                value={annBody}
                onChange={(e) => setAnnBody(e.target.value)}
                rows={3}
                placeholder="Announcement message body..."
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500 resize-none"
                required
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-emerald-500 text-black font-bold rounded-xl hover:bg-emerald-400 flex items-center space-x-1.5"
            >
              <Sparkles className="w-4 h-4" />
              <span>Broadcast to #official-announcements</span>
            </button>
          </form>

          <div className="space-y-2">
            <h4 className="text-xs text-gray-400 font-mono">Recent Official Announcements</h4>
            {announcements.map((a) => (
              <div key={a.id} className="p-3.5 rounded-xl bg-[#12141c] border border-[#1f2433] flex items-start justify-between text-xs">
                <div className="space-y-1">
                  <span className="font-bold text-white text-sm">{a.body.split('\n')[0].replace(/^##\s*/, '')}</span>
                  <p className="text-gray-400 text-xs">{a.body.replace(/^##[^\n]*\n*/, '')}</p>
                  <span className="text-[10px] text-gray-500 font-mono">{new Date(a.createdAt).toLocaleString()}</span>
                </div>
                <button
                  onClick={() => handleDeleteAnnouncementAction(a.id)}
                  className="p-1.5 text-gray-400 hover:text-rose-400 rounded-lg hover:bg-rose-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. Missions & Submissions Tab */}
      {activeTab === 'missions' && (
        <div className="space-y-4">
          <form onSubmit={handleCreateMissionSubmit} className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] space-y-3 text-xs">
            <h3 className="font-bold text-white text-sm flex items-center space-x-2">
              <Target className="w-4 h-4 text-indigo-400" />
              <span>Create New Society Mission</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-300 font-semibold mb-1">Title</label>
                <input
                  type="text"
                  value={newMissionTitle}
                  onChange={(e) => setNewMissionTitle(e.target.value)}
                  placeholder="Scavenger Hunt / Cryptography"
                  className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 font-semibold mb-1">Points Award</label>
                <input
                  type="number"
                  value={newMissionPoints}
                  onChange={(e) => setNewMissionPoints(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                  min={10}
                  max={500}
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Brief Description</label>
              <textarea
                value={newMissionBrief}
                onChange={(e) => setNewMissionBrief(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500 resize-none"
                placeholder="Mission overview and objectives..."
                required
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-400"
            >
              Publish Mission
            </button>
          </form>

          <div className="space-y-2">
            <h4 className="text-xs text-gray-400 font-mono">Pending Mission Submissions for Review</h4>
            {submissions.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-xs bg-[#12141c] rounded-xl border border-[#1f2433]">
                No pending mission submissions.
              </div>
            ) : (
              submissions.map((sub) => (
                <div key={sub.id} className="p-3.5 rounded-xl bg-[#12141c] border border-[#1f2433] space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white">{sub.mission_title}</span>
                    <span className="font-mono text-amber-400">+{sub.points} pts</span>
                  </div>
                  <div className="text-gray-400 font-mono text-[11px]">
                    Submitter: {sub.submitter_alias || 'Member'} (#{sub.submitter_member_number || '---'})
                  </div>
                  <p className="p-2 rounded-lg bg-[#090a0f] text-gray-300 border border-[#1f2433] font-mono text-xs">
                    {sub.submission_text}
                  </p>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[10px] text-gray-500 font-mono">{new Date(sub.created_at).toLocaleString()}</span>
                    {sub.status === 'submitted' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleReviewSubmissionAction(sub.id, true)}
                          className="px-3 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 font-semibold"
                        >
                          Approve & Award
                        </button>
                        <button
                          onClick={() => handleReviewSubmissionAction(sub.id, false)}
                          className="px-3 py-1 rounded-lg bg-rose-500/20 text-rose-400 border border-rose-500/30 hover:bg-rose-500/30 font-semibold"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 7. Events & Trips Tab */}
      {activeTab === 'events' && (
        <form onSubmit={handleCreateEventSubmit} className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] space-y-3 text-xs">
          <h3 className="font-bold text-white text-sm flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-cyan-400" />
            <span>Publish Society Event or Campus Trip</span>
          </h3>

          <div className="flex space-x-3">
            <label className="flex items-center space-x-1.5 text-gray-300">
              <input
                type="radio"
                checked={newEvCategory === 'event'}
                onChange={() => setNewEvCategory('event')}
              />
              <span>Campus Gathering</span>
            </label>
            <label className="flex items-center space-x-1.5 text-gray-300">
              <input
                type="radio"
                checked={newEvCategory === 'trip'}
                onChange={() => setNewEvCategory('trip')}
              />
              <span>Outdoor Trip / Expedition</span>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Title</label>
              <input
                type="text"
                value={newEvTitle}
                onChange={(e) => setNewEvTitle(e.target.value)}
                placeholder="e.g. Dawn Heritage Photowalk"
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Approximate Location</label>
              <input
                type="text"
                value={newEvLocation}
                onChange={(e) => setNewEvLocation(e.target.value)}
                placeholder="Central Courtyard / Hillside Trail"
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Capacity</label>
              <input
                type="number"
                value={newEvCapacity}
                onChange={(e) => setNewEvCapacity(Number(e.target.value))}
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                min={2}
                max={500}
              />
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Days Ahead</label>
              <input
                type="number"
                value={newEvDaysAhead}
                onChange={(e) => setNewEvDaysAhead(Number(e.target.value))}
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                min={1}
                max={60}
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-300 font-semibold mb-1">Description & Safety Notes</label>
            <textarea
              value={newEvDesc}
              onChange={(e) => setNewEvDesc(e.target.value)}
              rows={3}
              placeholder="Event details, schedule, requirements..."
              className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500 resize-none"
              required
            />
          </div>

          <button
            type="submit"
            className="px-4 py-2 bg-cyan-500 text-black font-bold rounded-xl hover:bg-cyan-400"
          >
            Publish {newEvCategory === 'trip' ? 'Trip' : 'Event'}
          </button>
        </form>
      )}

      {/* 8. Invites Manager Tab */}
      {activeTab === 'invites' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-[#12141c] border border-[#1f2433]">
            <div>
              <h3 className="font-bold text-white text-sm flex items-center space-x-2">
                <Key className="w-4 h-4 text-emerald-400" />
                <span>Society Invitations</span>
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Generate cryptographic perimeter passes for single or batch onboarding.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setBatchResult(null);
                setBatchConfirming(false);
                setBatchError(null);
                setShowBatchModal(true);
              }}
              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-bold rounded-xl text-xs flex items-center justify-center space-x-2 shadow-lg shadow-emerald-500/10 active:scale-95 transition-all"
            >
              <Layers className="w-4 h-4" />
              <span>Batch Generate Invites</span>
            </button>
          </div>

          {/* Batch Generation Modal */}
          {showBatchModal && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
              <div className="w-full max-w-xl bg-[#12141c] border border-[#1f2433] rounded-3xl p-6 shadow-2xl space-y-4 my-8 relative animate-fade-in">
                <div className="flex items-center justify-between border-b border-[#1f2433] pb-3">
                  <div className="flex items-center space-x-2.5">
                    <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                      <Layers className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm">Batch Invitation Generator</h3>
                      <p className="text-[11px] text-gray-400 font-mono">Atomic multi-token issuance & deep linking</p>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseBatchModal}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-[#1f2433] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Error Banner */}
                {batchError && (
                  <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs">
                    {batchError}
                  </div>
                )}

                {/* Feedback Banner */}
                {batchFeedback && (
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono">
                    {batchFeedback}
                  </div>
                )}

                {/* State 1: Generation Successful View */}
                {batchResult ? (
                  <div className="space-y-4">
                    {/* Security Credential Warning */}
                    <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-amber-300 text-xs flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-400 mt-0.5" />
                      <div className="space-y-1">
                        <div className="font-bold text-amber-200">SECURITY NOTICE: Exported Codes Are Credentials</div>
                        <p className="text-[11px] text-amber-400/90 leading-relaxed">
                          These invitation codes grant direct perimeter access to ROOM 404. Treat exported CSV files and deep links as confidential credentials. Store them securely and distribute privately. Raw secret tokens cannot be retrieved once this window is closed.
                        </p>
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-[#090a0f] border border-[#1f2433] text-xs font-mono text-gray-300 flex items-center justify-between">
                      <div>
                        <span className="text-emerald-400 font-bold">{batchResult.count}</span> tokens generated for{' '}
                        <span className="text-white font-bold">{batchResult.cohort}</span> ({batchResult.role})
                      </div>
                      <div className="text-[11px] text-gray-500">
                        Expires: {new Date(batchResult.expiresAt).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Action Buttons: CSV Export & Copy All */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <button
                        type="button"
                        onClick={handleExportBatchCsv}
                        className="py-2.5 px-3 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl text-xs flex items-center justify-center space-x-2 transition-all shadow-lg shadow-emerald-500/10 active:scale-95"
                      >
                        <Download className="w-4 h-4" />
                        <span>Export Secure CSV</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleCopyAllDeepLinks}
                        className="py-2.5 px-3 bg-[#1f2433] hover:bg-[#282f42] text-white font-bold rounded-xl text-xs flex items-center justify-center space-x-2 border border-[#2e374d] transition-all active:scale-95"
                      >
                        <Copy className="w-4 h-4 text-cyan-400" />
                        <span>Copy All Deep Links</span>
                      </button>
                    </div>

                    {/* Scrollable Tokens Preview Table */}
                    <div className="space-y-1.5">
                      <div className="text-[11px] font-mono text-gray-400 flex justify-between px-1">
                        <span>Generated Codes Preview ({batchResult.invites.length})</span>
                        <span>Click code to copy</span>
                      </div>
                      <div className="max-h-56 overflow-y-auto space-y-1.5 p-2 bg-[#090a0f] border border-[#1f2433] rounded-xl font-mono text-xs">
                        {batchResult.invites.map((inv, idx) => (
                          <div
                            key={inv.id || idx}
                            className="p-2 rounded-lg bg-[#12141c] border border-[#1a1f2c] flex items-center justify-between hover:border-emerald-500/40 transition-colors"
                          >
                            <span className="text-emerald-400 font-bold">{inv.code}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-[10px] text-gray-500">Uses: {inv.maxUses}</span>
                              <button
                                type="button"
                                onClick={() => handleCopySingleCode(inv.code)}
                                className="px-2 py-0.5 rounded bg-[#1f2433] hover:bg-[#282f42] text-[10px] text-gray-300 font-sans flex items-center space-x-1"
                              >
                                <Copy className="w-3 h-3" />
                                <span>Copy</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-2 border-t border-[#1f2433] flex justify-end">
                      <button
                        type="button"
                        onClick={handleCloseBatchModal}
                        className="px-5 py-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl text-xs transition-colors"
                      >
                        Done & Close
                      </button>
                    </div>
                  </div>
                ) : batchConfirming ? (
                  /* State 2: Explicit Confirmation Before Generation */
                  <div className="space-y-4">
                    <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300 space-y-2">
                      <h4 className="font-bold text-amber-200 text-sm flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        <span>Confirm Batch Generation</span>
                      </h4>
                      <p className="text-[11px] text-amber-400/90 leading-relaxed">
                        Are you sure you want to generate <strong className="text-white">{batchCount}</strong> invites for{' '}
                        <strong className="text-white">{batchCohort}</strong> with role{' '}
                        <strong className="text-white">{batchRole}</strong>?
                      </p>
                    </div>

                    <div className="p-3.5 rounded-xl bg-[#090a0f] border border-[#1f2433] space-y-2 text-xs font-mono">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total Tokens:</span>
                        <span className="text-emerald-400 font-bold">{batchCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Assigned Role:</span>
                        <span className="text-white">{batchRole}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Cohort Tag:</span>
                        <span className="text-white">{batchCohort}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Max Uses Per Token:</span>
                        <span className="text-white">{batchMaxUses}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Expiration:</span>
                        <span className="text-white">{batchDays} days</span>
                      </div>
                      {batchNotes && (
                        <div className="flex justify-between pt-1 border-t border-[#1f2433]">
                          <span className="text-gray-400">Notes:</span>
                          <span className="text-gray-300 italic">{batchNotes}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-end space-x-2.5 pt-2 border-t border-[#1f2433]">
                      <button
                        type="button"
                        onClick={() => setBatchConfirming(false)}
                        disabled={batchSubmitting}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 font-semibold rounded-xl text-xs transition-colors"
                      >
                        Back to Edit
                      </button>
                      <button
                        type="button"
                        onClick={handleExecuteBatchGeneration}
                        disabled={batchSubmitting}
                        className="px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl text-xs flex items-center space-x-2 transition-all disabled:opacity-50"
                      >
                        <span>{batchSubmitting ? 'Generating Atomically...' : 'Confirm & Generate Batch'}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* State 3: Batch Parameters Input Form */
                  <form onSubmit={handleStartBatchGeneration} className="space-y-3.5 text-xs">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-300 font-semibold mb-1">Number of Invites (1-100)</label>
                        <input
                          type="number"
                          required
                          min={1}
                          max={100}
                          value={batchCount}
                          onChange={(e) => setBatchCount(Number(e.target.value))}
                          className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500 font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-300 font-semibold mb-1">Role Granted</label>
                        <select
                          value={batchRole}
                          onChange={(e) => setBatchRole(e.target.value)}
                          className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                        >
                          <option value="member">Member</option>
                          <option value="initiate">Initiate</option>
                          <option value="room_host">Room Host</option>
                          <option value="event_lead">Event Lead</option>
                          <option value="curator">Curator</option>
                          <option value="moderator">Moderator</option>
                          <option value="verifier">Verifier</option>
                          <option value="dean">Dean</option>
                          <option value="administrator">Administrator</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-gray-300 font-semibold mb-1">Max Uses Per Code</label>
                        <input
                          type="number"
                          min={1}
                          max={500}
                          value={batchMaxUses}
                          onChange={(e) => setBatchMaxUses(Number(e.target.value))}
                          className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500 font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-300 font-semibold mb-1">Cohort Tag</label>
                        <input
                          type="text"
                          required
                          value={batchCohort}
                          onChange={(e) => setBatchCohort(e.target.value)}
                          placeholder="Cohort 404"
                          className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-300 font-semibold mb-1">Validity (Days)</label>
                        <input
                          type="number"
                          min={1}
                          max={365}
                          value={batchDays}
                          onChange={(e) => setBatchDays(Number(e.target.value))}
                          className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500 font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-300 font-semibold mb-1">Notes / Issuance Purpose (Optional)</label>
                      <input
                        type="text"
                        value={batchNotes}
                        onChange={(e) => setBatchNotes(e.target.value)}
                        placeholder="e.g. Fall 2026 CS Orientation / Hackathon VIPs"
                        maxLength={200}
                        className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                      />
                    </div>

                    <div className="flex items-center justify-end space-x-2.5 pt-3 border-t border-[#1f2433]">
                      <button
                        type="button"
                        onClick={handleCloseBatchModal}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 font-semibold rounded-xl text-xs transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-bold rounded-xl text-xs flex items-center space-x-2 transition-all shadow-lg shadow-emerald-500/10 active:scale-95"
                      >
                        <span>Review & Generate</span>
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}

          {/* Single Invite Generator Form */}
          <form onSubmit={handleGenerateInviteSubmit} className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] space-y-3 text-xs">
            <h3 className="font-bold text-white text-sm flex items-center space-x-2">
              <Key className="w-4 h-4 text-emerald-400" />
              <span>Generate Single Society Invitation Token</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-300 font-semibold mb-1">Max Uses</label>
                <input
                  type="number"
                  value={inviteUses}
                  onChange={(e) => setInviteUses(Number(e.target.value))}
                  min={1}
                  max={500}
                  className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-gray-300 font-semibold mb-1">Validity (Days)</label>
                <input
                  type="number"
                  value={inviteDays}
                  onChange={(e) => setInviteDays(Number(e.target.value))}
                  min={1}
                  max={365}
                  className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-emerald-500"
                />
              </div>
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-emerald-500 text-black font-bold rounded-xl hover:bg-emerald-400"
            >
              Generate Cryptographic Invite
            </button>

            {generatedInviteCode && (
              <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl font-mono text-emerald-400 text-sm select-all">
                {generatedInviteCode}
              </div>
            )}
          </form>

          <div className="space-y-2">
            <h4 className="text-xs text-gray-400 font-mono">Active Invitation Codes</h4>
            {invites.map((inv) => (
              <div key={inv.id} className="p-3.5 rounded-xl bg-[#12141c] border border-[#1f2433] flex items-center justify-between text-xs">
                <div className="space-y-0.5">
                  <div className="font-mono text-white font-bold flex items-center">
                    <span>{inv.id.slice(0, 16)}...</span>
                    {inv.role && (
                      <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded font-sans uppercase ml-1.5">
                        {inv.role}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] font-mono text-gray-400">
                    Uses: {inv.use_count} / {inv.max_uses} • Expires: {new Date(inv.expires_at).toLocaleDateString()}
                    {inv.cohort ? ` • ${inv.cohort}` : ''}
                  </div>
                </div>
                {inv.is_revoked ? (
                  <span className="px-2 py-0.5 rounded bg-rose-500/10 text-rose-400 text-[10px] font-mono">REVOKED</span>
                ) : (
                  <button
                    onClick={() => handleRevokeInviteAction(inv.id)}
                    className="px-2.5 py-1 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs font-semibold border border-rose-500/30"
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 9. Audited Identity Unmask Tab */}
      {activeTab === 'unmask' && (
        <div className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] space-y-4 text-xs">
          <div className="flex items-center space-x-2 text-amber-400">
            <ShieldAlert className="w-5 h-5" />
            <h3 className="font-bold text-sm">Audited Private Identity Access</h3>
          </div>
          <p className="text-gray-400 text-xs leading-relaxed">
            Unmasking student identity records is restricted by policy and requires a documented reason, verified MFA confirmation, and creates an immutable append-only audit entry.
          </p>

          <form onSubmit={handleUnmaskIdentity} className="space-y-3">
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Target User ID (UUID)</label>
              <input
                type="text"
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white font-mono outline-none focus:border-amber-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Reason for Unmasking (min 10 characters)</label>
              <textarea
                value={unmaskReason}
                onChange={(e) => setUnmaskReason(e.target.value)}
                placeholder="Official student safety or legal inquiry reason..."
                rows={2}
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none focus:border-amber-500 resize-none"
                required
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-amber-500 text-black font-bold rounded-xl hover:bg-amber-400 flex items-center space-x-1.5"
            >
              <Lock className="w-4 h-4" />
              <span>Decrypt & View Audited Student Identity</span>
            </button>
          </form>

          {unmaskError && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs font-semibold">
              {unmaskError}
            </div>
          )}

          {unmaskedData && (
            <div className="p-4 rounded-xl bg-[#090a0f] border border-amber-500/40 space-y-1.5 font-mono text-xs text-gray-300">
              <div className="text-amber-400 font-bold mb-2">Decrypted Identity Record:</div>
              <div>Legal Name: <span className="text-white font-bold">{unmaskedData.legalName}</span></div>
              <div>Enrollment Number: <span className="text-white">{unmaskedData.enrollmentNumber}</span></div>
              <div>Faculty Number: <span className="text-white">{unmaskedData.facultyNumber}</span></div>
              <div>College: <span className="text-white">{unmaskedData.facultyCollege}</span></div>
              <div>Course: <span className="text-white">{unmaskedData.courseBranch} ({unmaskedData.admissionYear})</span></div>
              <div>Personal Email: <span className="text-white">{unmaskedData.personalEmail}</span></div>
            </div>
          )}
        </div>
      )}

      {/* 10. Free Tier Limits / Telemetry Tab */}
      {activeTab === 'telemetry' && telemetry && (
        <div className="space-y-3">
          <div className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] space-y-3 text-xs">
            <h3 className="font-bold text-white text-sm flex items-center space-x-2">
              <Activity className="w-4 h-4 text-emerald-400" />
              <span>Real-Time Cloudflare Free-Tier Headroom</span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-xl bg-[#090a0f] border border-[#1f2433]">
                <div className="text-gray-400 text-[11px]">Worker Requests</div>
                <div className="text-base font-bold text-white font-mono mt-1">
                  {telemetry.freeTierUsage.workerRequests} / 100K
                </div>
                <div className="text-emerald-400 text-[10px] font-mono">
                  {telemetry.freeTierUsage.percentages.workerRequests}% used
                </div>
              </div>
              <div className="p-3 rounded-xl bg-[#090a0f] border border-[#1f2433]">
                <div className="text-gray-400 text-[11px]">D1 Rows Read</div>
                <div className="text-base font-bold text-white font-mono mt-1">
                  {telemetry.freeTierUsage.d1RowsRead} / 5M
                </div>
                <div className="text-emerald-400 text-[10px] font-mono">
                  {telemetry.freeTierUsage.percentages.d1RowsRead}% used
                </div>
              </div>
              <div className="p-3 rounded-xl bg-[#090a0f] border border-[#1f2433]">
                <div className="text-gray-400 text-[11px]">D1 Rows Written</div>
                <div className="text-base font-bold text-white font-mono mt-1">
                  {telemetry.freeTierUsage.d1RowsWritten} / 100K
                </div>
                <div className="text-emerald-400 text-[10px] font-mono">
                  {telemetry.freeTierUsage.percentages.d1RowsWritten}% used
                </div>
              </div>
              <div className="p-3 rounded-xl bg-[#090a0f] border border-[#1f2433]">
                <div className="text-gray-400 text-[11px]">Durable Objects</div>
                <div className="text-base font-bold text-white font-mono mt-1">
                  {telemetry.freeTierUsage.doRequests} / 1M
                </div>
                <div className="text-emerald-400 text-[10px] font-mono">
                  {telemetry.freeTierUsage.percentages.doRequests}% used
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Assignment Modal */}
      {selectedMemberForRole && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <form onSubmit={handleExecuteRoleMutation} className="w-full max-w-sm bg-[#121520] border border-[#23293d] rounded-2xl p-5 space-y-4 shadow-2xl text-xs">
            <div className="flex items-center justify-between border-b border-[#23293d] pb-3">
              <h3 className="text-sm font-bold text-white">Manage Operational Roles</h3>
              <button type="button" onClick={() => setSelectedMemberForRole(null)} className="text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <span className="text-gray-400">Target Member: </span>
              <span className="font-bold text-white">{selectedMemberForRole.alias}</span> (#{selectedMemberForRole.memberNumber})
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Select Role</label>
              <select
                value={roleToAssign}
                onChange={(e) => setRoleToAssign(e.target.value)}
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none"
              >
                <option value="moderator">Moderator</option>
                <option value="verifier">Verifier</option>
                <option value="curator">Curator</option>
                <option value="event_lead">Event Lead</option>
                <option value="room_host">Room Host</option>
                <option value="administrator">Administrator</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Action</label>
              <div className="flex space-x-3">
                <label className="flex items-center space-x-1 text-gray-300">
                  <input
                    type="radio"
                    checked={roleAction === 'grant'}
                    onChange={() => setRoleAction('grant')}
                  />
                  <span>Grant Role</span>
                </label>
                <label className="flex items-center space-x-1 text-gray-300">
                  <input
                    type="radio"
                    checked={roleAction === 'revoke'}
                    onChange={() => setRoleAction('revoke')}
                  />
                  <span>Revoke Role</span>
                </label>
              </div>
            </div>
            <div className="pt-2 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setSelectedMemberForRole(null)}
                className="px-3 py-1.5 bg-[#1a1f30] text-gray-300 rounded-lg hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 bg-emerald-500 text-black font-semibold rounded-lg hover:bg-emerald-400"
              >
                Confirm Role Mutation
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Room Permissions Modal */}
      {selectedRoomForPerms && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <form onSubmit={handleSaveRoomPerms} className="w-full max-w-sm bg-[#121520] border border-[#23293d] rounded-2xl p-5 space-y-4 shadow-2xl text-xs">
            <div className="flex items-center justify-between border-b border-[#23293d] pb-3">
              <h3 className="text-sm font-bold text-white">Room Role Permissions</h3>
              <button type="button" onClick={() => setSelectedRoomForPerms(null)} className="text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <span className="text-gray-400">Configuring: </span>
              <span className="font-bold text-white">#{selectedRoomForPerms.name}</span>
            </div>
            <div>
              <label className="block text-gray-300 font-semibold mb-1">Target Role</label>
              <select
                value={roomPermRole}
                onChange={(e) => setRoomPermRole(e.target.value)}
                className="w-full px-3 py-2 bg-[#090a0f] border border-[#23293d] rounded-xl text-white outline-none"
              >
                <option value="member">Member</option>
                <option value="moderator">Moderator</option>
                <option value="host">Host</option>
              </select>
            </div>
            <div className="space-y-2 pt-1">
              <label className="flex items-center space-x-2 text-gray-200">
                <input type="checkbox" checked={canView} onChange={(e) => setCanView(e.target.checked)} />
                <span>Can View Room</span>
              </label>
              <label className="flex items-center space-x-2 text-gray-200">
                <input type="checkbox" checked={canPost} onChange={(e) => setCanPost(e.target.checked)} />
                <span>Can Post New Messages</span>
              </label>
              <label className="flex items-center space-x-2 text-gray-200">
                <input type="checkbox" checked={canReply} onChange={(e) => setCanReply(e.target.checked)} />
                <span>Can Reply to Messages</span>
              </label>
              <label className="flex items-center space-x-2 text-gray-200">
                <input type="checkbox" checked={canReact} onChange={(e) => setCanReact(e.target.checked)} />
                <span>Can React with Emojis</span>
              </label>
              <label className="flex items-center space-x-2 text-gray-200">
                <input type="checkbox" checked={canUpload} onChange={(e) => setCanUpload(e.target.checked)} />
                <span>Can Upload Attachments</span>
              </label>
            </div>
            <div className="pt-2 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setSelectedRoomForPerms(null)}
                className="px-3 py-1.5 bg-[#1a1f30] text-gray-300 rounded-lg hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 bg-emerald-500 text-black font-semibold rounded-lg hover:bg-emerald-400"
              >
                Save Room Policy
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Evidence Review Modal */}
      {selectedAppForEvidence && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121520] border border-[#23293d] rounded-2xl p-5 space-y-4 shadow-2xl text-xs max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-[#23293d] pb-3">
              <h3 className="text-sm font-bold text-white">Student e-ID Verification Review</h3>
              <button
                type="button"
                onClick={() => {
                  setSelectedAppForEvidence(null);
                  setEvidenceBlobUrl(null);
                  setDeletionProofMessage(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-1">
              <div className="text-sm font-bold text-white">{selectedAppForEvidence.legalName}</div>
              <div className="text-gray-400 font-mono text-[11px]">
                {selectedAppForEvidence.college} • {selectedAppForEvidence.course} ({selectedAppForEvidence.admissionYear})
              </div>
            </div>

            <div className="border border-[#23293d] rounded-xl overflow-hidden bg-black/60 min-h-[160px] flex items-center justify-center">
              {evidenceBlobUrl ? (
                <img src={evidenceBlobUrl} alt="Student e-ID" className="max-h-[300px] object-contain w-full" />
              ) : (
                <div className="text-gray-500 font-mono text-xs">Streaming private verification evidence...</div>
              )}
            </div>

            {deletionProofMessage && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 text-xs font-mono">
                {deletionProofMessage}
              </div>
            )}

            <div className="flex space-x-2 pt-2">
              <button
                disabled={reviewLoading}
                onClick={() => handleReviewAction('approved')}
                className="flex-1 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold flex items-center justify-center space-x-1"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Approve & Purge e-ID</span>
              </button>
              <button
                disabled={reviewLoading}
                onClick={() => handleReviewAction('rejected')}
                className="flex-1 py-2 rounded-xl bg-rose-500 hover:bg-rose-400 text-white font-bold flex items-center justify-center space-x-1"
              >
                <Trash2 className="w-4 h-4" />
                <span>Reject & Purge e-ID</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
